/**
 * The run labels shared by the run card and the past-runs table.
 *
 * These live here rather than in `lib/run-view.ts` — where the rest of this domain's formatting is —
 * only because that module is the shared seam another vertical also owns. Everything else about them
 * follows its rules: the platform's `trigger`/`sync_type` are narrowed by `run-view` first and never
 * compared to a literal, and a timestamp is validated before it is formatted.
 */
import dayjs from 'dayjs';

import { normalizeRunType, normalizeTrigger } from '../lib/run-view';
import type { RawSyncRun, RunTrigger, RunType } from '../types';

export const RUN_TRIGGER_LABEL: Record<RunTrigger, string> = {
  manual: 'Manual',
  scheduled: 'Scheduled',
};

export const RUN_TYPE_LABEL: Record<RunType, string> = {
  full: 'Full',
  incremental: 'Incremental',
};

export function runTriggerLabel(run: RawSyncRun): string {
  return RUN_TRIGGER_LABEL[normalizeTrigger(run.trigger)];
}

export function runTypeLabel(run: RawSyncRun): string {
  return RUN_TYPE_LABEL[normalizeRunType(run.sync_type)];
}

/**
 * When the run started, in the viewer's timezone.
 *
 * Guarded, because `dayjs` renders an unparseable input as the literal string `Invalid Date` and
 * every timestamp here comes off a platform whose payload shape `types.ts` documents as untrusted.
 * `runDurationSeconds` already guards the same field; this is the label beside it.
 */
export function runStartedLabel(run: RawSyncRun): string {
  const started = dayjs(run.started_at);
  return started.isValid() ? started.format('MMM DD YYYY [at] h:mma') : 'Unknown start time';
}
